import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import type { ReactNode } from 'react';

import {
  type GeneratedContentType,
  GeneratedContentTypeEnum,
} from '../components/savi-content-panels/types';
import {
  type AskApiResponse,
  AskApiResponseDefaultValues,
  type Dashboard,
  type SampleDataAPIResponse,
} from '../data-layer/types';

export interface MessageBase {
  id: string;
  timestamp: number;
  chatuuid: string | null;
}

export interface UserMessage extends MessageBase {
  isUser: true; // isUser is true
  text: string; // text is mandatory when isUser is true
  isDividerRequired?: boolean;
}

export interface SaviMessage extends MessageBase {
  data: AskApiResponse | SampleDataAPIResponse; // data is mandatory when isUser is false
  isUser: false; // isUser is false
  text?: string; // text is optional when isUser is false
  isSaveToChart?: boolean;
}

// Combine both types into a single Message type
export type Message = UserMessage | SaviMessage;

// Define the shape of the context state
export interface ChatOverlayState {
  saviInputText: string;
  showGeneratedContent: boolean;
  messages: Message[];
  clearChat: boolean;
  contentPanelKind: GeneratedContentType;
  contentPanelData: AskApiResponse | SampleDataAPIResponse;
  inputDisabled: boolean;
  isApiCallInProgress: boolean;
  isWarningModalOpen: boolean;
  isDataPanelFullScreen: boolean;
  dashboardLists: Dashboard[];
  successAction?: {
    // set when an action is successful such as a clipboard copy or download
    type: string; // 'key' for the successful action
  };
}

// Define a generic action that automatically infers from ISPMState
interface Action<K extends keyof ChatOverlayState> {
  type: K;
  value: ChatOverlayState[K];
}

// Initial state
export const initialState: ChatOverlayState = {
  saviInputText: '',
  showGeneratedContent: false,
  messages: [],
  clearChat: false,
  contentPanelKind: GeneratedContentTypeEnum.GENERATING_CONTENT,
  contentPanelData: AskApiResponseDefaultValues,
  inputDisabled: false,
  isApiCallInProgress: false,
  isWarningModalOpen: false,
  isDataPanelFullScreen: false,
  dashboardLists: [],
  successAction: undefined,
};

// Reducer function that infers the key and value types
const reducer = <K extends keyof ChatOverlayState>(
  state: ChatOverlayState,
  action: Action<K>
): ChatOverlayState => ({ ...state, [action.type]: action.value });

// Create the context with a default value
const ChatOverlayContext = createContext<
  | {
      chatOverlayState: ChatOverlayState;
      updateChatOverlayState: <K extends keyof ChatOverlayState>(
        key: K,
        value: ChatOverlayState[K]
      ) => void;
    }
  | undefined
>(undefined);

// Provider component that wraps the children
export function ChatOverlayProvider({ children }: { children: ReactNode }) {
  const [chatOverlayState, dispatch] = useReducer(reducer, initialState);

  // Function to dispatch actions based on key and value, with no hardcoding
  const updateChatOverlayState = useCallback(
    <K extends keyof ChatOverlayState>(key: K, value: ChatOverlayState[K]) => {
      dispatch({ type: key, value }); // No hardcoded strings!
    },
    []
  );

  // Memoize the context value to avoid unnecessary re-renders
  const value = useMemo(
    () => ({
      chatOverlayState,
      updateChatOverlayState,
    }),
    [chatOverlayState, updateChatOverlayState]
  );

  return (
    <ChatOverlayContext.Provider value={value}>
      {children}
    </ChatOverlayContext.Provider>
  );
}

// Custom hook to use the ISPM context
export const useChatOverlayContext = () => {
  const context = useContext(ChatOverlayContext);

  if (!context) {
    throw new Error(
      'useChatOverlayContext must be used within an ChatOverlayProvider'
    );
  }

  return context;
};
